import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Radio, Layers, Cpu, Satellite } from "lucide-react";
import PhotonicHero from "../components/hero/PhotonicHero";
import VideoHero from "../components/hero/VideoHero";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import { useSection } from "../context/SiteContentContext";
import SEO from "../components/common/SEO";

const DEFAULT_HOME = {
    hero_eyebrow: "System 01 · Skovomax · EST 2024",
    hero_line1: "From Sensors",
    hero_line2: "to Systems Intelligence.",
    hero_body:
        "Skovomax integrates GeoAI, LiDAR, machine learning and spatial interaction models to understand, predict, and manage complex real-world systems — from photon to policy.",
    hero_meta: [
        { label: "SYS", value: "Photon-Grade" },
        { label: "STACK", value: "LiDAR · GeoAI" },
        { label: "MODE", value: "Systems Thinking" },
        { label: "OUTPUT", value: "Decision Intelligence" },
    ],
    stats: [
        { value: "97%", label: "Flood Prediction Accuracy", sub: "Ensemble ML · U.S. ACE" },
        { value: "1550", label: "nm Fiber Laser", sub: "Eye-safe coherent sensing" },
        { value: "LOD2–5", label: "3D Reconstruction", sub: "LiDAR-derived models" },
        { value: "TB/day", label: "Geospatial Pipeline", sub: "Multi-sensor fusion" },
    ],
};

const TECH = [
    {
        icon: Radio,
        tag: "TECH · 01",
        title: "LiDAR Systems",
        copy:
            "Photon timing and interaction measurement. Emission, propagation, reflection, detection, and reconstruction — treated end-to-end as an optical system.",
        to: "/technologies#lidar",
        thumb: "/api/uploads/6a030c594aed6d35557e8fb7",
    },
    {
        icon: Cpu,
        tag: "TECH · 02",
        title: "Photonic Sensors",
        copy:
            "Closed-loop optical architectures: laser source, beam shaping, target interaction, photodetection, and signal processing for time-of-flight and phase.",
        to: "/technologies#sensors",
        thumb: "/api/uploads/6a042810a3991be6fb4e7ad2",
    },
    {
        icon: Layers,
        tag: "TECH · 03",
        title: "Fiber Laser Systems",
        copy:
            "Rare-earth doped fibers (Yb³⁺, Er³⁺, Tm³⁺) as gain medium. High efficiency, stable beam quality, and scalable power for coherent LiDAR and mid-IR sensing.",
        to: "/technologies#fiber-laser",
        thumb: "/api/uploads/6a043e19c14735fbb53a7f10",
    },
    {
        icon: Satellite,
        tag: "TECH · 04",
        title: "GeoAI Analytics",
        copy:
            "Machine learning applied to spatial data — point clouds, hyperspectral imagery, SAR, climate records — to extract structure, pattern and decision signals.",
        to: "/technologies#geoai",
        thumb: "/api/uploads/6a044713c14735fbb53a7f18",
    },
];

const PILLARS = [
    {
        n: "01",
        title: "Physical World Sensing & Reconstruction",
        body: "LiDAR, hyperspectral data, and sensor fusion to reconstruct real environments in high resolution. 3D modeling (LOD2–LOD5), utility corridor analysis, vegetation–infrastructure interaction, digital terrain and surface models.",
    },
    {
        n: "02",
        title: "GeoAI & Pattern Intelligence",
        body: "Machine learning to extract hidden structure from spatial data. Feature extraction from imagery, classification from point clouds, deep learning for geoscientific discovery.",
    },
    {
        n: "03",
        title: "Climate & Hazard Systems Modeling",
        body: "Predictive models for environmental risk — flood, wildfire ignition, levee failure, extreme events — integrating ML with SAR, remote sensing and topographic factors.",
    },
    {
        n: "04",
        title: "Human–Environment Interaction",
        body: "Modeling how human activity shapes spatial outcomes — disease spread, mobility-driven risk, socio-economic vulnerability, spatial diffusion.",
    },
];

const HERO_VIDEO_URL =
    "https://customer-assets.emergentagent.com/job_sensor-intelligence/artifacts/3w5y0w45_Laser_beam_entering_202604211423.mp4";

const DEFAULT_SERVICES = [
    { n: "01", title: "Sensor Integration & Calibration", body: "Photonic LiDAR, hyperspectral and multispectral payload integration with radiometric and geometric calibration." },
    { n: "02", title: "Point Cloud & Imagery Processing", body: "Classification, filtering, LOD2–LOD5 reconstruction, vectorization, and fusion with spectral data." },
    { n: "03", title: "GeoAI Model Development", body: "Bespoke ML pipelines — CNNs, ensembles, SVMs, spatial interaction models — with rigorous validation." },
    { n: "04", title: "Hazard & Decision Systems", body: "Flood, wildfire, levee, disease, and infrastructure risk pipelines with MCDA decision frameworks." },
];

export default function Home() {
    const home = useSection("home", DEFAULT_HOME);
    const allServices = useSection("services", null);
    const STATS = home.stats || DEFAULT_HOME.stats;
    const SERVICES_PREVIEW = allServices && allServices.length
        ? allServices.slice(0, 4).map((s, i) => ({
              n: s.n || String(i + 1).padStart(2, "0"),
              title: s.title,
              body: s.body,
          }))
        : DEFAULT_SERVICES;
    return (
        <>
            <SEO
                title="Skovomax — From Sensors to Systems Intelligence"
                description="Skovomax builds photonic sensing platforms, LiDAR systems, fiber lasers and GeoAI analytics — integrated into real-world decision systems for industry, government and research."
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    name: "Skovomax Engineering LLP",
                    url: typeof window !== "undefined" ? window.location.origin : undefined,
                    slogan: "From Sensors to Systems Intelligence",
                    description:
                        "Advanced sensing and systems-intelligence company. LiDAR systems, photonic sensors, fiber laser sources, and GeoAI analytics.",
                    sameAs: [],
                }}
            />
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-center overflow-hidden">
                <VideoHero src={HERO_VIDEO_URL}>
                    <PhotonicHero />
                </VideoHero>
                <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 w-full">
                    {home.hero_eyebrow ? (
                        <Reveal>
                            <div className="overline mb-5 flex items-center gap-2" data-testid="hero-label">
                                <span className="section-dot" />
                                {home.hero_eyebrow}
                            </div>
                        </Reveal>
                    ) : null}
                    <Reveal delay={120}>
                        <h1 className="font-serif-display text-[52px] sm:text-[80px] lg:text-[104px] leading-[0.95] tracking-tight max-w-5xl">
                            {home.hero_line1}
                            <br />
                            <span className="italic text-white/75">{home.hero_line2}</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={240}>
                        <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed">
                            {home.hero_body}
                        </p>
                    </Reveal>
                    <Reveal delay={360}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/technologies" className="btn-primary" data-testid="hero-cta-primary">
                                Explore Technologies
                                <ArrowUpRight size={14} />
                            </Link>
                            <Link to="/contact" className="btn-secondary" data-testid="hero-cta-secondary">
                                Request Demo
                            </Link>
                        </div>
                    </Reveal>

                    {/* Hero meta readout */}
                    <Reveal delay={480}>
                        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 font-mono-tech text-[11px] tracking-[0.18em] uppercase text-white/50 border-t border-white/10 pt-6">
                            {(home.hero_meta || DEFAULT_HOME.hero_meta).map((m, i) => (
                                <div key={i}>
                                    <div className="text-white/30">//{m.label}</div>
                                    <div className="text-white mt-1">{m.value}</div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Stats */}
            <section className="py-24 border-t border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12">
                        {STATS.map((s, i) => (
                            <Reveal delay={i * 80} key={s.label}>
                                <div data-testid={`stat-${i}`}>
                                    <div className="font-serif-display text-[56px] leading-none text-[var(--accent-mercury)]">
                                        {s.value}
                                    </div>
                                    <div className="mt-3 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/70">
                                        {s.label}
                                    </div>
                                    <div className="mt-1 text-sm text-white/45">{s.sub}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies */}
            <section id="technologies" className="py-28 border-t border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                        <div className="md:col-span-5">
                            <SectionLabel number="01" label="Technologies" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                Four systems. One measurement philosophy.
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/65 leading-relaxed text-lg">
                                We treat sensors as light-based measurement systems — emission, interaction,
                                detection, timing, reconstruction — and build the analytics layer that turns
                                those measurements into structured intelligence.
                            </p>
                        </div>
                    </div>

                    <div>
                        {TECH.map((t, i) => {
                            const imgRight = i % 2 === 0;
                            return (
                                <Reveal delay={i * 90} key={t.title}>
                                    <Link
                                        to={t.to}
                                        className="block group bg-[#050510] hover:bg-[#0e0e1c] transition-colors"
                                        data-testid={`tech-card-${i}`}
                                    >
                                        <div className={`grid grid-cols-1 md:grid-cols-12 gap-0 md:gap-10 items-stretch ${imgRight ? "" : "md:[&>*:first-child]:order-2"}`}>
                                            {/* Text column */}
                                            <div className="md:col-span-6 lg:col-span-5 p-8 md:p-12 lg:p-14 flex flex-col">
                                                <div className="flex items-start gap-4 mb-6">
                                                    <t.icon size={22} className="text-[var(--accent-indigo)] mt-1" />
                                                    <div className="tech-label">{t.tag}</div>
                                                </div>
                                                <h3 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight mb-5">
                                                    {t.title}
                                                </h3>
                                                <p className="text-white/65 leading-relaxed text-[15.5px] md:text-base max-w-md">
                                                    {t.copy}
                                                </p>
                                                <span
                                                    className="inline-flex items-center gap-2 mt-10 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/70 group-hover:text-[var(--accent-indigo)] transition-colors"
                                                    data-testid={`tech-card-link-${i}`}
                                                >
                                                    View system <ArrowUpRight size={12} />
                                                </span>
                                            </div>
                                            {/* Image column */}
                                            <div className="md:col-span-6 lg:col-span-7 relative bg-[#050510] min-h-[320px] md:min-h-[560px] overflow-hidden">
                                                {t.thumb && (
                                                    <img
                                                        src={`${process.env.REACT_APP_BACKEND_URL || ""}${t.thumb}`}
                                                        alt={`${t.title} — anatomy preview`}
                                                        className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                                                        loading="lazy"
                                                        data-testid={`tech-card-thumb-${i}`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Pillars / What we actually do */}
            <section className="py-28 border-t border-white/10 bg-[#0a0a18]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                        <div className="md:col-span-5">
                            <SectionLabel number="02" label="What we actually do" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                Systems thinking, <br />
                                <span className="italic text-white/70">photon to policy.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/60 leading-relaxed text-lg">
                                We do not sell modules. We design closed loops — where sensing, modeling, and
                                decision-making are engineered as a single continuous system.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {PILLARS.map((p, i) => (
                            <Reveal delay={i * 80} key={p.n}>
                                <TechCard className="!p-10 h-full">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--accent-indigo)]">
                                            {p.n} / Pillar
                                        </div>
                                        <div className="w-3 h-3 border border-[var(--accent-indigo)]" />
                                    </div>
                                    <h3 className="font-serif-display text-2xl mb-4 leading-tight">{p.title}</h3>
                                    <p className="text-white/60 leading-relaxed">{p.body}</p>
                                </TechCard>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services preview */}
            <section className="py-28 border-t border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 mb-14">
                        <div>
                            <SectionLabel number="03" label="Services" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                                Engagements, <span className="italic text-white/70">engineered.</span>
                            </h2>
                        </div>
                        <Link to="/services" className="btn-secondary" data-testid="home-services-cta">
                            All services
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {SERVICES_PREVIEW.map((s, i) => (
                            <Reveal delay={i * 80} key={s.title}>
                                <Link to="/services" className="block">
                                    <TechCard className="!p-8 h-full">
                                        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--accent-indigo)] mb-6">
                                            {s.n} / Service
                                        </div>
                                        <h3 className="font-serif-display text-2xl leading-tight mb-4">
                                            {s.title}
                                        </h3>
                                        <p className="text-white/55 text-sm leading-relaxed">{s.body}</p>
                                        <ArrowUpRight
                                            size={16}
                                            className="mt-8 text-white/40 group-hover:text-[var(--accent-indigo)]"
                                        />
                                    </TechCard>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing CTA */}
            <section className="py-28 border-t border-white/10 bg-[#0a0a18]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                            <div className="md:col-span-8">
                                <SectionLabel number="04" label="Engage" />
                                <h2 className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tight">
                                    Have a system that needs to measure reality?
                                </h2>
                            </div>
                            <div className="md:col-span-4 md:justify-self-end">
                                <Link to="/contact" className="btn-primary" data-testid="home-cta-inquire">
                                    Start an Inquiry
                                    <ArrowUpRight size={14} />
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
